import { defineStore } from "pinia";
import { store } from "../index";
import { setCssVar, humpToUnderline } from "@/utils";
import { ElMessage, ComponentSize } from "element-plus";
import { colorIsDark, hexToRGB, lighten, mix } from "@/utils/color";
import { unref } from "vue";
import { useCssVar } from "@vueuse/core";
import { useDark } from "@vueuse/core";
import { useI18n } from "@/hooks/web/useI18n";
const { t } = useI18n();

// Store 版本号，当默认值发生变化时，需要更新此版本号
const STORE_VERSION = "1.0.5";

interface AppState {
  storeVersion: string; // Store 版本号，用于迁移
  breadcrumb: boolean;
  breadcrumbIcon: boolean;
  collapse: boolean;
  uniqueOpened: boolean;
  hamburger: boolean;
  screenfull: boolean;
  size: boolean;
  locale: boolean;
  tagsView: boolean;
  tagsViewIcon: boolean;
  logo: boolean;
  fixedHeader: boolean;
  greyMode: boolean;
  dynamicRouter: boolean;
  serverDynamicRouter: boolean;
  pageLoading: boolean;
  layout: LayoutType;
  title: string;
  isDark: boolean;
  currentSize: ComponentSize;
  sizeMap: ComponentSize[];
  mobile: boolean;
  footer: boolean;
  theme: ThemeTypes;
  fixedMenu: boolean;
}

// Layout is controlled by code; keep other persisted preferences unchanged.
const appStateSerializer = {
  serialize(state: Partial<AppState>) {
    const persistedState = { ...state };
    delete persistedState.layout;
    return JSON.stringify(persistedState);
  },
  deserialize(value: string): Partial<AppState> {
    const persistedState = JSON.parse(value);
    delete persistedState.layout;
    return persistedState;
  },
};

// 获取默认状态
const getDefaultState = (): Omit<AppState, "storeVersion"> => {
  return {
    sizeMap: ["default", "large", "small"],
    mobile: false, // 是否是移动端
    title: "OrbitHub - Command Your Machine Universe", // 标题
    pageLoading: false, // 路由跳转loading
    breadcrumb: true, // 面包屑
    breadcrumbIcon: true, // 面包屑图标
    collapse: false, // 折叠菜单
    uniqueOpened: true, // 是否只保持一个子菜单的展开
    hamburger: true, // 折叠图标
    screenfull: false, // 全屏图标
    size: false, // 尺寸图标
    locale: true, // 多语言图标
    tagsView: false, // 标签页
    tagsViewIcon: false, // 是否显示标签图标
    logo: true, // logo
    fixedHeader: true, // 固定toolheader
    footer: true, // 显示页脚
    greyMode: false, // 是否开始灰色模式，用于特殊悼念日
    dynamicRouter: true, // 是否动态路由
    serverDynamicRouter: true, // 是否服务端渲染动态路由
    fixedMenu: false, // 是否固定菜单

    layout: "top", // layout布局
    isDark: false, // 是否是暗黑模式
    currentSize: "default", // 组件尺寸
    theme: {
      // 主题色
      elColorPrimary: "#60f8b6",
      // 左侧菜单边框颜色
      leftMenuBorderColor: "#eee",
      // 左侧菜单背景颜色
      leftMenuBgColor: "#ffffff",
      // 左侧菜单浅色背景颜色
      leftMenuBgLightColor: "#ffffff",
      // 左侧菜单选中背景颜色
      leftMenuBgActiveColor: "var(--el-color-primary)",
      // 左侧菜单收起选中背景颜色
      leftMenuCollapseBgActiveColor: "var(--el-color-primary)",
      // 左侧菜单字体颜色
      leftMenuTextColor: "#666666",
      // 左侧菜单选中字体颜色
      leftMenuTextActiveColor: "#fff",
      // logo字体颜色
      logoTitleTextColor: "#666666",
      // logo边框颜色
      logoBorderColor: "inherit",
      // 头部背景颜色
      topHeaderBgColor: "#f7f7f7",
      // 头部字体颜色
      topHeaderTextColor: "inherit",
      // 头部悬停颜色
      topHeaderHoverColor: "#f6f6f6",
      // 头部边框颜色
      topToolBorderColor: "#eee",
    },
  };
};

// 深度合并对象，只合并新字段，保留用户已设置的值
const deepMergeDefaults = (
  existing: Partial<AppState>,
  defaults: Omit<AppState, "storeVersion">,
): AppState => {
  const merged = { ...defaults } as AppState;

  // 合并基本字段：如果用户已设置（包括 false/0/'' 等 falsy 值），保留用户的值；否则使用默认值
  // 只有字段不存在（不存在于 existing 中）或值为 undefined 时，才使用默认值
  for (const key in defaults) {
    if (key === "layout") continue;
    const keyTyped = key as keyof Omit<AppState, "storeVersion">;

    // 检查字段是否存在于 existing 中，且值不是 undefined
    // 注意：对于布尔值 false，这也是一个有效的用户设置，应该保留
    if (key in existing) {
      const existingValue = existing[keyTyped];

      // 如果值不是 undefined，说明用户已经设置过（包括 false、0、'' 等）
      if (existingValue !== undefined) {
        // 对于 theme 对象，需要特殊处理（深度合并）
        if (key === "theme" && existing.theme && defaults.theme) {
          merged.theme = { ...defaults.theme, ...existing.theme } as ThemeTypes;
        } else {
          // 直接使用用户的值（包括 false、0、'' 等）
          (merged as any)[keyTyped] = existingValue;
        }
      }
      // 如果 existingValue === undefined，说明字段存在但值是 undefined，使用默认值
    }
    // 如果 key 不在 existing 中，说明是新字段或用户从未设置过，使用默认值（已在 merged 中）
  }

  return merged;
};

export const useAppStore = defineStore("app", {
  state: (): AppState => {
    // 获取默认状态
    const defaultState = getDefaultState();

    // 返回默认状态，版本迁移会在 $onAction 或初始化后处理
    return {
      storeVersion: STORE_VERSION,
      ...defaultState,
    };
  },
  getters: {
    getBreadcrumb(): boolean {
      return this.breadcrumb;
    },
    getBreadcrumbIcon(): boolean {
      return this.breadcrumbIcon;
    },
    getCollapse(): boolean {
      return this.collapse;
    },
    getUniqueOpened(): boolean {
      return this.uniqueOpened;
    },
    getHamburger(): boolean {
      return this.hamburger;
    },
    getScreenfull(): boolean {
      return this.screenfull;
    },
    getSize(): boolean {
      return this.size;
    },
    getLocale(): boolean {
      return this.locale;
    },
    getTagsView(): boolean {
      return this.tagsView;
    },
    getTagsViewIcon(): boolean {
      return this.tagsViewIcon;
    },
    getLogo(): boolean {
      return this.logo;
    },
    getFixedHeader(): boolean {
      return this.fixedHeader;
    },
    getGreyMode(): boolean {
      return this.greyMode;
    },
    getDynamicRouter(): boolean {
      return this.dynamicRouter;
    },
    getServerDynamicRouter(): boolean {
      return this.serverDynamicRouter;
    },
    getFixedMenu(): boolean {
      return this.fixedMenu;
    },
    getPageLoading(): boolean {
      return this.pageLoading;
    },
    getLayout(): LayoutType {
      return this.layout;
    },
    getTitle(): string {
      return this.title;
    },
    getIsDark(): boolean {
      return this.isDark;
    },
    getCurrentSize(): ComponentSize {
      return this.currentSize;
    },
    getSizeMap(): ComponentSize[] {
      return this.sizeMap;
    },
    getMobile(): boolean {
      return this.mobile;
    },
    getTheme(): ThemeTypes {
      return this.theme;
    },
    getFooter(): boolean {
      return this.footer;
    },
  },
  actions: {
    setBreadcrumb(breadcrumb: boolean) {
      this.breadcrumb = breadcrumb;
    },
    setBreadcrumbIcon(breadcrumbIcon: boolean) {
      this.breadcrumbIcon = breadcrumbIcon;
    },
    setCollapse(collapse: boolean) {
      this.collapse = collapse;
    },
    setUniqueOpened(uniqueOpened: boolean) {
      this.uniqueOpened = uniqueOpened;
    },
    setHamburger(hamburger: boolean) {
      this.hamburger = hamburger;
    },
    setScreenfull(screenfull: boolean) {
      this.screenfull = screenfull;
    },
    setSize(size: boolean) {
      this.size = size;
    },
    setLocale(locale: boolean) {
      this.locale = locale;
    },
    setTagsView(tagsView: boolean) {
      this.tagsView = tagsView;
    },
    setTagsViewIcon(tagsViewIcon: boolean) {
      this.tagsViewIcon = tagsViewIcon;
    },
    setLogo(logo: boolean) {
      this.logo = logo;
    },
    setFixedHeader(fixedHeader: boolean) {
      this.fixedHeader = fixedHeader;
    },
    setGreyMode(greyMode: boolean) {
      this.greyMode = greyMode;
    },
    setDynamicRouter(dynamicRouter: boolean) {
      this.dynamicRouter = dynamicRouter;
    },
    setServerDynamicRouter(serverDynamicRouter: boolean) {
      this.serverDynamicRouter = serverDynamicRouter;
    },
    setFixedMenu(fixedMenu: boolean) {
      this.fixedMenu = fixedMenu;
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = pageLoading;
    },
    setLayout(layout: LayoutType) {
      if (this.mobile && layout !== "classic" && layout !== "top") {
        ElMessage.warning(t("error.mobileLayoutWarning"));
        return;
      }
      this.layout = layout;
    },
    setTitle(title: string) {
      this.title = title;
    },
    setIsDark(isDark: boolean) {
      this.isDark = isDark;
      if (this.isDark) {
        document.documentElement.classList.add("dark");
        document.documentElement.classList.remove("light");
        // 设置暗黑模式的CSS变量
        setCssVar("--top-header-bg-color", "#111827");
        setCssVar("--top-header-text-color", "#f9fafb");
        setCssVar("--top-header-hover-color", "#374151");

        // 暗黑模式 - Logo和菜单区域
        setCssVar("--left-menu-bg-color", "#1f2937");
        setCssVar("--left-menu-bg-light-color", "#374151");
        setCssVar("--left-menu-text-color", "#d1d5db");
        setCssVar("--left-menu-text-active-color", "#10b981");
        setCssVar("--left-menu-bg-active-color", "rgba(16, 185, 129, 0.15)");
        setCssVar(
          "--left-menu-collapse-bg-active-color",
          "rgba(16, 185, 129, 0.15)",
        );
      } else {
        document.documentElement.classList.add("light");
        document.documentElement.classList.remove("dark");
        // 设置明亮模式的CSS变量
        setCssVar("--top-header-bg-color", "#f7f7f7");
        setCssVar("--top-header-text-color", "#374151");
        setCssVar("--top-header-hover-color", "#f6f6f6");

        // 明亮模式 - Logo和菜单区域
        setCssVar("--left-menu-bg-color", "#f8f9fa");
        setCssVar("--left-menu-bg-light-color", "#f8f9fa");
        setCssVar("--left-menu-text-color", "#666666");
        setCssVar("--left-menu-text-active-color", "#10b981");
        setCssVar("--left-menu-bg-active-color", "rgba(16, 185, 129, 0.15)");
        setCssVar(
          "--left-menu-collapse-bg-active-color",
          "rgba(16, 185, 129, 0.15)",
        );
      }

      // 强制设置Element Plus主题色
      setCssVar("--el-color-primary", "#60f8b6");
      this.setPrimaryLight();
    },
    setCurrentSize(currentSize: ComponentSize) {
      this.currentSize = currentSize;
    },
    setMobile(mobile: boolean) {
      this.mobile = mobile;
    },
    setTheme(theme: ThemeTypes) {
      this.theme = Object.assign(this.theme, theme);
    },
    setCssVarTheme() {
      // 设置Element Plus主题色
      setCssVar("--el-color-primary", this.theme.elColorPrimary);

      for (const key in this.theme) {
        setCssVar(`--${humpToUnderline(key)}`, this.theme[key]);
      }
      this.setPrimaryLight();
    },
    setFooter(footer: boolean) {
      this.footer = footer;
    },
    setPrimaryLight() {
      if (this.theme.elColorPrimary) {
        const elColorPrimary = this.theme.elColorPrimary;
        const color = this.isDark ? "#000000" : "#ffffff";
        const lightList = [3, 5, 7, 8, 9];
        lightList.forEach((v) => {
          setCssVar(
            `--el-color-primary-light-${v}`,
            mix(color, elColorPrimary, v / 10),
          );
        });
        setCssVar(`--el-color-primary-dark-2`, mix(color, elColorPrimary, 0.2));

        // 设置Element Plus的其他主题色变量
        setCssVar("--el-color-primary", elColorPrimary);
        setCssVar(
          "--el-color-primary-light-3",
          mix(color, elColorPrimary, 0.3),
        );
        setCssVar(
          "--el-color-primary-light-5",
          mix(color, elColorPrimary, 0.5),
        );
        setCssVar(
          "--el-color-primary-light-7",
          mix(color, elColorPrimary, 0.7),
        );
        setCssVar(
          "--el-color-primary-light-8",
          mix(color, elColorPrimary, 0.8),
        );
        setCssVar(
          "--el-color-primary-light-9",
          mix(color, elColorPrimary, 0.9),
        );
      }
    },
    setMenuTheme(color: string) {
      const primaryColor = useCssVar(
        "--el-color-primary",
        document.documentElement,
      );
      const isDarkColor = colorIsDark(color);
      const theme: Recordable = {
        // 左侧菜单边框颜色
        leftMenuBorderColor: isDarkColor ? "inherit" : "#eee",
        // 左侧菜单背景颜色
        leftMenuBgColor: color,
        // 左侧菜单浅色背景颜色
        leftMenuBgLightColor: isDarkColor ? lighten(color!, 6) : color,
        // 左侧菜单选中背景颜色
        leftMenuBgActiveColor: isDarkColor
          ? "var(--el-color-primary)"
          : hexToRGB(unref(primaryColor), 0.1),
        // 左侧菜单收起选中背景颜色
        leftMenuCollapseBgActiveColor: isDarkColor
          ? "var(--el-color-primary)"
          : hexToRGB(unref(primaryColor), 0.1),
        // 左侧菜单字体颜色
        leftMenuTextColor: isDarkColor ? "#bfcbd9" : "#666666",
        // 左侧菜单选中字体颜色
        leftMenuTextActiveColor: isDarkColor
          ? "#fff"
          : "var(--el-color-primary)",
        // logo字体颜色
        logoTitleTextColor: isDarkColor ? "#fff" : "inherit",
        // logo边框颜色
        logoBorderColor: isDarkColor ? color : "#eee",
      };
      this.setTheme(theme);
      this.setCssVarTheme();
    },
    setHeaderTheme(color: string) {
      const isDarkColor = colorIsDark(color);
      const textColor = isDarkColor ? "#fff" : "inherit";
      const textHoverColor = isDarkColor ? lighten(color!, 6) : "#f6f6f6";
      const topToolBorderColor = isDarkColor ? color : "#eee";
      setCssVar("--top-header-bg-color", color);
      setCssVar("--top-header-text-color", textColor);
      setCssVar("--top-header-hover-color", textHoverColor);
      this.setTheme({
        topHeaderBgColor: color,
        topHeaderTextColor: textColor,
        topHeaderHoverColor: textHoverColor,
        topToolBorderColor,
      });
      if (this.getLayout === "top") {
        this.setMenuTheme(color);
      }
    },
    initTheme() {
      const isDark = useDark({
        valueDark: "dark",
        valueLight: "light",
      });
      isDark.value = this.getIsDark;

      // 强制设置Element Plus主题色
      setCssVar("--el-color-primary", "#60f8b6");

      // 初始化头部主题
      if (this.getIsDark) {
        // 暗黑模式
        setCssVar("--top-header-bg-color", "#111827");
        setCssVar("--top-header-text-color", "#f9fafb");
        setCssVar("--top-header-hover-color", "#374151");

        // 暗黑模式 - Logo和菜单区域
        setCssVar("--left-menu-bg-color", "#1f2937");
        setCssVar("--left-menu-bg-light-color", "#374151");
        setCssVar("--left-menu-text-color", "#d1d5db");
        setCssVar("--left-menu-text-active-color", "#ffffff");
        setCssVar("--left-menu-bg-active-color", "#10b981");
        setCssVar("--left-menu-collapse-bg-active-color", "#10b981");
      } else {
        // 明亮模式
        setCssVar("--top-header-bg-color", "#f7f7f7");
        setCssVar("--top-header-text-color", "#374151");
        setCssVar("--top-header-hover-color", "#f6f6f6");

        // 明亮模式 - Logo和菜单区域
        // 使用 setMenuTheme 来设置白色主题，确保所有相关颜色都正确设置
        this.setMenuTheme("#ffffff");
      }

      // 设置Element Plus主题色的各种变体
      this.setPrimaryLight();
    },
    // 版本迁移方法：在应用初始化时调用（备用方案）
    migrateStoreVersion() {
      if (this.storeVersion !== STORE_VERSION) {
        // 使用 console.warn 确保在生产环境也能看到
        console.warn("[Store Migration] migrateStoreVersion called:", {
          currentVersion: this.storeVersion,
          targetVersion: STORE_VERSION,
          currentTagsView: this.tagsView,
        });
        const defaultState = getDefaultState();
        const migratedState = deepMergeDefaults(this.$state, defaultState);
        console.warn("[Store Migration] After migrateStoreVersion:", {
          migratedTagsView: migratedState.tagsView,
          defaultTagsView: defaultState.tagsView,
        });
        migratedState.storeVersion = STORE_VERSION;
        // 使用 $patch 更新状态，避免触发不必要的响应
        this.$patch(migratedState);
      }
    },
  },
  persist: {
    // 持久化所有字段（包括 storeVersion）
    // Layout is excluded on both read and write, including legacy cached values.
    serializer: appStateSerializer,
    // 使用 beforeRestore 钩子在恢复数据之前检查版本并执行迁移
    beforeRestore: (ctx) => {
      // 直接从 localStorage 读取原始数据（因为 ctx.store.$state 此时可能还是默认值）
      let persistedState: Partial<AppState> | null = null;
      try {
        const stored = localStorage.getItem("app");
        if (stored) {
          persistedState = appStateSerializer.deserialize(stored);
        }
      } catch (e) {
        console.warn(
          "[Store Migration] beforeRestore: Failed to parse localStorage:",
          e,
        );
        return; // 如果解析失败，让插件使用默认值
      }

      if (!persistedState) {
        console.warn(
          "[Store Migration] beforeRestore: No persisted state found",
        );
        return; // 如果没有持久化数据，使用默认值
      }

      const defaultState = getDefaultState();

      // 检查版本号
      if (
        !persistedState.storeVersion ||
        persistedState.storeVersion !== STORE_VERSION
      ) {
        console.warn(
          "[Store Migration] beforeRestore: Version mismatch detected:",
          {
            oldVersion: persistedState.storeVersion || "undefined",
            newVersion: STORE_VERSION,
            existingTagsView: persistedState.tagsView,
            defaultTagsView: defaultState.tagsView,
          },
        );

        // 执行迁移：使用新默认值，而不是保留旧值
        // 当版本更新时，应用新的默认值，确保用户获得最新的默认配置
        // 只保留用户个性化配置（主题、布局、暗黑模式等），其他配置类选项使用新默认值
        const migrated: AppState = {
          storeVersion: STORE_VERSION,
          ...defaultState, // 先使用新默认值（包括 tagsView: false）
          // 保留运行时状态（这些是用户当前操作状态，应该保留）
          collapse: persistedState.collapse ?? defaultState.collapse,
          pageLoading: persistedState.pageLoading ?? defaultState.pageLoading,
          mobile: persistedState.mobile ?? defaultState.mobile,
          // 保留用户个性化配置（主题、外观等）
          theme: persistedState.theme
            ? { ...defaultState.theme, ...persistedState.theme }
            : defaultState.theme,
          isDark: persistedState.isDark ?? defaultState.isDark,
          currentSize: persistedState.currentSize ?? defaultState.currentSize,
          // layout: persistedState.layout ?? defaultState.layout,
          // Layout now always uses the code default, not a user preference.
          // 注意：tagsView、breadcrumb、logo 等配置类选项使用新默认值，不保留旧值
        } as AppState;

        console.warn("[Store Migration] beforeRestore: Migration completed:", {
          migratedTagsView: migrated.tagsView,
          storeVersion: migrated.storeVersion,
          note: "Using new default values instead of preserving old settings",
        });

        // 更新即将恢复的状态
        Object.assign(ctx.store.$state, migrated);

        // 立即保存迁移后的状态到 localStorage
        try {
          localStorage.setItem("app", appStateSerializer.serialize(migrated));
        } catch (e) {
          console.warn(
            "[Store Migration] beforeRestore: Failed to save migrated state:",
            e,
          );
        }
      } else {
        // 版本匹配，但需要确保状态正确恢复
        // 直接将持久化的状态恢复到 store
        console.warn(
          "[Store Migration] beforeRestore: Version matched, restoring state:",
          {
            version: persistedState.storeVersion,
            tagsView: persistedState.tagsView,
          },
        );

        // 确保所有字段都被正确恢复（包括可能的缺失字段）
        const restoredState = {
          storeVersion: STORE_VERSION,
          ...defaultState,
          ...persistedState,
        } as AppState;

        Object.assign(ctx.store.$state, restoredState);
      }
    },
  },
});

export const useAppStoreWithOut = () => {
  return useAppStore(store);
};
